<template>
  <div class="remark-field-tags">
    <v-chip-group :model-value="selectedIds" multiple column selected-class="selected-chip" @update:model-value="updateSelected">
      <v-chip
        v-for="opt in options"
        :key="opt.id"
        :value="opt.id"
        :disabled="isChipDisabled(opt)"
        :class="['wd-border-black50 wd-mr-2 wd-mb-2 wd-text-base', { 'wd-cursor-pointer': !isChipDisabled(opt) }]"
        variant="outlined"
        pill
        label
      >
        {{ opt.value }}
      </v-chip>
    </v-chip-group>

    <!-- custom input block -->
    <div v-if="showCustomInput" class="wd-mt-2">
      <v-textarea
        v-model="customInputValue"
        :placeholder="customInputPlaceholder"
        color="orange50"
        auto-grow
        rows="1"
        variant="outlined"
        density="compact"
        hide-details="auto"
        v-on="compositionEvents"
        @paste="handlePaste"
        @update:model-value="updateCustomInput"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { FormFieldOptionCreationData } from '@/view-models/remark'

type Props = {
  options: Array<{ id: number; value: string; is_custom_input?: boolean; placeholder?: string }>
  limitMax?: number
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  limitMax: undefined,
})

const modelValue = defineModel<FormFieldOptionCreationData[]>({ default: () => [] })

const selectedIds = computed(() => modelValue.value.map(item => item.sub_id))

const hasReachedLimit = computed(() => {
  if (!props.limitMax) return false
  return selectedIds.value.length >= props.limitMax
})

function isChipDisabled(opt: { id: number }) {
  if (!hasReachedLimit.value) return false
  return !selectedIds.value.includes(opt.id)
}

const customInputOption = computed(() => props.options.find(opt => opt.is_custom_input))
const customInputPlaceholder = computed(() => customInputOption.value?.placeholder || '')

const showCustomInput = computed(() => {
  if (!customInputOption.value) return false
  return selectedIds.value.includes(customInputOption.value.id)
})

const customInputValue = ref('')

const MAX_CUSTOM_INPUT_LENGTH = 40
const { isComposing, events: compositionEvents } = useCompositionEvent()
const compositionStartLength = ref<number | null>(null)

const { handlePaste } = usePaste({
  maxTextLength: MAX_CUSTOM_INPUT_LENGTH,
  getCurrentValue: () => (typeof customInputValue.value === 'string' ? customInputValue.value : ''),
  onPasteComplete: (newValue: string) => {
    updateCustomInput(newValue)
  },
})

watchEffect(() => {
  if (customInputOption.value) {
    const customItem = modelValue.value.find(item => item.sub_id === customInputOption.value!.id)
    if (customItem && customItem.content !== customInputValue.value) {
      customInputValue.value = customItem.content
    }
  }
})

watch(isComposing, (composing, prevComposing) => {
  if (!prevComposing && composing) {
    compositionStartLength.value = typeof customInputValue.value === 'string' ? customInputValue.value.length : 0
    return
  }

  if (prevComposing && !composing) {
    const startLen = compositionStartLength.value
    compositionStartLength.value = null

    const currentLen = typeof customInputValue.value === 'string' ? customInputValue.value.length : 0
    if (startLen !== null && startLen <= MAX_CUSTOM_INPUT_LENGTH && currentLen > MAX_CUSTOM_INPUT_LENGTH) {
      updateCustomInput(customInputValue.value)
    }
  }
})

function updateSelected(ids: number[]) {
  if (props.limitMax && ids.length > props.limitMax) {
    return
  }

  const result = props.options
    .filter(option => ids.includes(option.id))
    .map(option => {
      if (option.is_custom_input) {
        return { sub_id: option.id, content: customInputValue.value || '' }
      }

      return { sub_id: option.id, content: '' }
    })

  modelValue.value = result
}

function updateCustomInput(value: string) {
  const normalizedValue = typeof value === 'string' ? value : ''

  if (isComposing.value) {
    customInputValue.value = normalizedValue

    if (customInputOption.value) {
      const newValue = modelValue.value.map(item => {
        if (item.sub_id === customInputOption.value!.id) {
          return { ...item, content: normalizedValue }
        }
        return item
      })
      modelValue.value = newValue
    }

    return
  }

  const nextValue = normalizedValue.length > MAX_CUSTOM_INPUT_LENGTH ? normalizedValue.slice(0, MAX_CUSTOM_INPUT_LENGTH) : normalizedValue

  customInputValue.value = nextValue

  if (customInputOption.value) {
    const newValue = modelValue.value.map(item => {
      if (item.sub_id === customInputOption.value!.id) {
        return { ...item, content: nextValue }
      }
      return item
    })
    modelValue.value = newValue
  }
}
</script>

<style lang="scss">
.remark-field-tags {
  .v-chip {
    @apply wd-border-gray50 !important;
  }
  .v-chip--disabled {
    @apply wd-opacity-100 wd-text-gray60 wd-cursor-not-allowed;
  }
  .v-chip__content {
    @apply wd-text-base wd-py-1;
  }
  .selected-chip {
    @apply wd-border-orange50 wd-text-orange50 !important;
  }
}
</style>
